<template>
  <div class="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
    <div class="flex flex-wrap items-center gap-3 text-sm">
      <template v-if="showSort && sortOptions.length">
        <span class="hidden sm:inline text-gray-600">정렬:</span>
        <select class="form-input-inline text-sm w-full sm:w-auto" :value="sortBy" @change="onSortChange">
          <option v-for="option in sortOptions" :key="option.value" :value="option.value">
            {{ option.label }}
          </option>
        </select>
      </template>
      <span v-if="showCount" class="hidden sm:inline text-gray-500">
        전체 {{ totalElements }}개의 {{ countLabel }}
      </span>
      <span v-if="showFilteredCount && filteredElements !== null" class="hidden sm:inline text-gray-400">
        필터 결과 {{ filteredElements }}개
      </span>
      <slot name="left" />
    </div>
    <div class="flex flex-wrap items-center gap-3 sm:justify-end">
      <template v-if="showViewMode && viewMode">
        <div class="inline-flex rounded-lg border border-gray-200 overflow-hidden">
          <button
            class="px-2 py-2 text-xs font-medium flex items-center gap-1 sm:px-3"
            :class="viewMode === 'list' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:text-gray-800'"
            @click="emitViewMode('list')"
          >
            <Bars3Icon class="w-4 h-4" />
            <span class="hidden sm:inline">목록</span>
          </button>
          <button
            class="px-2 py-2 text-xs font-medium flex items-center gap-1 sm:px-3"
            :class="viewMode === 'grid' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:text-gray-800'"
            @click="emitViewMode('grid')"
          >
            <Squares2X2Icon class="w-4 h-4" />
            <span class="hidden sm:inline">그리드</span>
          </button>
        </div>
      </template>

      <template v-if="showPageSize && pageSizeOptions.length">
        <div class="flex items-center gap-2 text-sm text-gray-600">
          <select class="form-input-inline text-sm w-full sm:w-auto" :value="pageSize" @change="onPageSizeChange">
            <option v-for="option in pageSizeOptions" :key="option" :value="option">
              {{ option }}
            </option>
          </select>
          <span class="hidden sm:inline">개 보기</span>
        </div>
      </template>
      <slot name="right" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { Bars3Icon, Squares2X2Icon } from '@heroicons/vue/24/outline'

interface SortOption {
  value: string
  label: string
}

interface Props {
  sortBy?: string
  sortOptions?: SortOption[]
  totalElements?: number
  filteredElements?: number | null
  countLabel?: string
  viewMode?: 'list' | 'grid' | null
  pageSize?: number
  pageSizeOptions?: number[]
  showSort?: boolean
  showViewMode?: boolean
  showPageSize?: boolean
  showCount?: boolean
  showFilteredCount?: boolean
}

withDefaults(defineProps<Props>(), {
  sortBy: '',
  sortOptions: () => [],
  totalElements: 0,
  filteredElements: null,
  countLabel: '게시글',
  viewMode: null,
  pageSize: 0,
  pageSizeOptions: () => [],
  showSort: false,
  showViewMode: false,
  showPageSize: false,
  showCount: true,
  showFilteredCount: false
})

const emit = defineEmits<{
  'update:sortBy': [value: string]
  'update:viewMode': [value: 'list' | 'grid']
  'update:pageSize': [value: number]
  'sort-change': [value: string]
  'page-size-change': [value: number]
}>()

const onSortChange = (event: Event) => {
  const target = event.target as HTMLSelectElement
  emit('update:sortBy', target.value)
  emit('sort-change', target.value)
}

const onPageSizeChange = (event: Event) => {
  const target = event.target as HTMLSelectElement
  const value = Number(target.value)
  emit('update:pageSize', value)
  emit('page-size-change', value)
}

const emitViewMode = (value: 'list' | 'grid') => {
  emit('update:viewMode', value)
}
</script>
